import { Fragment, useRef, useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import {
    ListSubheader,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Stack,
    Typography,
    Backdrop,
    Box,
    Divider,
    IconButton,
    Tooltip,
    Button
} from '@mui/material';

import SimPagination from '../pagination';
import { NoData } from '../error';
import { LoadingScreen } from '../loading-screen';

import { SvgChevronDownFill, SvgChevronUpFill, SvgWarningFill } from '../SvgIcons';
import { DescriptionItem, DescriptionLabel, DescriptionValue } from '../Description';
import Label from '../label';
import { HISTORY_STATUS, ORDER_CHANGE_TYPE, ORDER_DETAILS_TYPE, TODO_STATUS, TODO_STATUS_CODE } from '../../store/types';
import { WaringFormOverlay } from '../toastify';
import { fDate } from '../../utils/formatTime';

const SimListApplyBItem = ({ item, action, variant = ORDER_DETAILS_TYPE.Apply }) => {
    const theme = useTheme();
    return (
        <ListItemButton
            disableGutters
            disableRipple
            sx={{ 
                padding: theme => theme.spacing(1.5, 2),
            }}
        >
            <ListItemText
                sx={{ m: 0 }}
                primaryTypographyProps={{
                    variant: 'h6',
                    mb: 0.5,
                }}
                secondaryTypographyProps={{
                    component: 'div',
                    sx: {
                        color: 'inherit'
                    }
                }}
                primary={
                    <Stack direction={'row'} gap={2} alignItems={'center'}>
                        {item?.rentType_dictText}申请
                        <Label color={TODO_STATUS?.[item?.status]} variant={'outlined'}>{item?.status_dictText}</Label>
                    </Stack>
                }
                secondary={
                    <Stack alignItems={'flex-start'} spacing={0.5}>
                        <Stack direction={'row'} alignItems={'center'} maxHeight={24}>
                            <DescriptionItem >
                                <DescriptionLabel>表單編號：</DescriptionLabel>
                                <DescriptionValue>{item?.applyNo || '--'}</DescriptionValue>
                            </DescriptionItem>
                            <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                            <DescriptionItem >
                                <DescriptionLabel>創建日期：</DescriptionLabel>
                                <DescriptionValue>{item?.createTime && fDate(item?.createTime, 'yyyy/MM/dd') || '--'} </DescriptionValue>
                            </DescriptionItem>
                            {
                                [
                                    TODO_STATUS_CODE.waitIn
                                ].includes(item?.status) && item?.waitNo && (
                                    <>
                                        <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                                        <DescriptionItem >
                                            <DescriptionLabel>來源單號：</DescriptionLabel>
                                            <DescriptionValue>{item?.waitNo || '--'}</DescriptionValue>
                                        </DescriptionItem>
                                    </>
                                )
                            }
                        </Stack>

                        <Stack direction={'row'} alignItems={'center'} maxHeight={24}>
                            <DescriptionItem>
                                <DescriptionLabel>廠區：</DescriptionLabel>
                                <DescriptionValue>{item?.factoryName || '--'}</DescriptionValue> 
                            </DescriptionItem>
                            {
                                [
                                    TODO_STATUS_CODE.signing, 
                                    TODO_STATUS_CODE.rejected,
                                    TODO_STATUS_CODE.terminated,
                                    TODO_STATUS_CODE.waitIn
                                ].includes(item?.status) && (
                                    <>
                                        <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                                        <DescriptionItem >
                                            <DescriptionLabel>需求日期：</DescriptionLabel>
                                            <DescriptionValue>{item?.startDate && fDate(item?.startDate, 'yyyy/MM/dd') || '--'}  - {item?.endDate && fDate(item?.endDate, 'yyyy/MM/dd')  || '--'}</DescriptionValue>
                                        </DescriptionItem>
                                    </>
                                )
                            }
                            {
                                [
                                    TODO_STATUS_CODE.lived, 
                                ].includes(item?.status) && (
                                    <>
                                        <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                                        <DescriptionItem >
                                            <DescriptionLabel>入駐日期：</DescriptionLabel>
                                            <DescriptionValue>{item?.actualLiveDate && fDate(item?.actualLiveDate, 'yyyy/MM/dd') || '--'}</DescriptionValue>
                                        </DescriptionItem>
                                        <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                                        <DescriptionItem >
                                            <DescriptionLabel>預計退租日期：</DescriptionLabel>
                                            <DescriptionValue>{item?.endDate && fDate(item?.endDate, 'yyyy/MM/dd') || '--'}</DescriptionValue>
                                        </DescriptionItem>
                                    </>
                                )
                            }
                        </Stack>
                    </Stack>
                }
            />
            {action}
        </ListItemButton>
    )
};


const SimListWaitBItem = ({ item, action, variant = ORDER_DETAILS_TYPE.Apply }) => {
    const theme = useTheme();
    return (
        <ListItemButton
            disableGutters
            disableRipple
            sx={{ 
                padding: theme => theme.spacing(1.5, 2),
            }}
        >
            <ListItemText
                sx={{ m: 0 }}
                primaryTypographyProps={{
                    variant: 'h6',
                    mb: 0.5,
                }}
                secondaryTypographyProps={{
                    component: 'div',
                    sx: {
                        color: 'inherit'
                    }
                }}
                primary={
                    <Stack direction={'row'} gap={2} alignItems={'center'}>
                        {item?.rentType_dictText}候補
                        <Label color={TODO_STATUS?.[item?.status]} variant={'outlined'}>{item?.status_dictText}</Label>
                    </Stack>
                }
                secondary={
                    <Stack alignItems={'flex-start'} spacing={0.5}>
                        <Stack direction={'row'} alignItems={'center'} maxHeight={24}>
                            <DescriptionItem >
                                <DescriptionLabel>表單編號：</DescriptionLabel>
                                <DescriptionValue>{item?.waitNo || '--'}</DescriptionValue>
                            </DescriptionItem>
                            <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                            <DescriptionItem >
                                <DescriptionLabel>創建日期：</DescriptionLabel>
                                <DescriptionValue>{item?.createTime && fDate(item?.createTime, 'yyyy/MM/dd') || '--'}</DescriptionValue>
                            </DescriptionItem>
                        </Stack>

                        <Stack direction={'row'} alignItems={'center'} maxHeight={24}>
                            <DescriptionItem>
                                <DescriptionLabel>廠區：</DescriptionLabel>
                                <DescriptionValue>{item?.factoryName || '--'}</DescriptionValue> 
                            </DescriptionItem>
                            <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                            <DescriptionItem>
                                <DescriptionLabel>需求日期：</DescriptionLabel>
                                <DescriptionValue>{item?.startDate && fDate(item?.startDate, 'yyyy/MM/dd') || '--'} - {item?.endDate && fDate(item?.endDate, 'yyyy/MM/dd') || '--'}</DescriptionValue>
                            </DescriptionItem>
                        </Stack>
                    </Stack>
                }
            />
            {action}
        </ListItemButton>
    )
};


const SimListDraftBItem = ({ item, action }) => {
    const theme = useTheme();
    return (
        <ListItemButton
            disableGutters
            disableRipple
            sx={{ 
                padding: theme => theme.spacing(1.5, 2),
            }}
        >
            <ListItemText
                sx={{ m: 0 }}
                primaryTypographyProps={{
                    variant: 'h6',
                    mb: 0.5,
                }}
                secondaryTypographyProps={{
                    component: 'div',
                    sx: {
                        color: 'inherit'
                    }
                }}
                primary={
                    <Stack direction={'row'} gap={2} alignItems={'center'}>
                        {item?.rentType_dictText}
                        <Label color={TODO_STATUS?.[item?.status]} variant={'outlined'}>{item?.status_dictText}</Label>
                    </Stack>
                }
                secondary={
                    <Stack direction={'row'} alignItems={'flex-start'} spacing={0.5}>
                        <DescriptionItem >
                            <DescriptionLabel>儲存日期：</DescriptionLabel>
                            <DescriptionValue>{(item?.updateTime ? fDate(item?.updateTime, 'yyyy/MM/dd') : fDate(item?.createTime, 'yyyy/MM/dd')) || '--'}</DescriptionValue>
                        </DescriptionItem>
                        <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                        <DescriptionItem>
                            <DescriptionLabel>廠區：</DescriptionLabel>
                            <DescriptionValue>{item?.factoryName || '--'}</DescriptionValue> 
                        </DescriptionItem>
                        <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                        <DescriptionItem>
                            <DescriptionLabel>需求日期：</DescriptionLabel>
                            <DescriptionValue>{item?.startDate && fDate(item?.startDate, 'yyyy/MM/dd') || '--'} - {item?.endDate && fDate(item?.endDate, 'yyyy/MM/dd') || '--'}</DescriptionValue>
                        </DescriptionItem>
                    </Stack>
                }
            />
            {action}
        </ListItemButton>
    )
};


// 历史表单 List
const SimListHistoryBItem = ({ item, action }) => {
    const theme = useTheme();
    return (
        <ListItemButton
            disableGutters
            disableRipple
            sx={{ 
                padding: theme => theme.spacing(1.5, 2),
            }}
        >
            <ListItemText
                sx={{ m: 0 }}
                primaryTypographyProps={{
                    variant: 'h6',
                    mb: 0.5,
                }}
                secondaryTypographyProps={{
                    component: 'div',
                    sx: {
                        color: 'inherit'
                    }
                }}
                primary={
                    <Stack direction={'row'} gap={2} alignItems={'center'}>
                        {item?.rentType_dictText}{ item?.applyNo?.includes('LA') ? '候補' : '申请'}
                        <Label color={HISTORY_STATUS?.[item?.status]} variant={'outlined'}>{item?.statusName}</Label>
                    </Stack>
                }
                secondary={
                    <Stack alignItems={'flex-start'} spacing={0.5}>
                        <Stack direction={'row'} alignItems={'center'} maxHeight={24}>

                            {/* 待入駐，已取消 */}
                            <DescriptionItem >
                                <DescriptionLabel>表單編號：</DescriptionLabel>
                                <DescriptionValue>{item?.applyNo || '--'}</DescriptionValue>
                            </DescriptionItem>

                            <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                            <DescriptionItem >
                                <DescriptionLabel>創建日期：</DescriptionLabel>
                                <DescriptionValue>{item?.createTime && fDate(item?.createTime, 'yyyy/MM/dd') || '--'}</DescriptionValue>
                            </DescriptionItem>

                            {/* 待入駐，已取消 來源LA */}
                            {
                                item?.applyNo?.includes('LA') && [TODO_STATUS_CODE.waitIn].includes(item?.status) && (
                                    <>
                                        <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                                        <DescriptionItem >
                                            <DescriptionLabel>來源單號：</DescriptionLabel>
                                            <DescriptionValue>{item?.waitNo || '--'}</DescriptionValue>
                                        </DescriptionItem>
                                    </>
                                )
                            }

                            {/* 候補成功 */}
                            {
                                item?.applyNo?.includes('LA') && [TODO_STATUS_CODE.waitToOk].includes(item?.status) && (
                                    <>
                                        <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                                        <DescriptionItem >
                                            <DescriptionLabel>入駐單號：</DescriptionLabel>
                                            <DescriptionValue>{item?.waitNo || '--'}</DescriptionValue>
                                        </DescriptionItem>
                                    </>
                                )
                            } 
                        </Stack>

                        <Stack direction={'row'} alignItems={'center'} maxHeight={24}>
                            <DescriptionItem>
                                <DescriptionLabel>廠區：</DescriptionLabel>
                                <DescriptionValue>{item?.factoryName || '--'}</DescriptionValue> 
                            </DescriptionItem>
                            
                            {/* 已退租，無變更  來源LA */}
                            {
                                [
                                    TODO_STATUS_CODE.refunded,
                                ].includes(item?.status) && (
                                    <>
                                        <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                                        <DescriptionItem>
                                            <DescriptionLabel>入駐日期：</DescriptionLabel>
                                            <DescriptionValue>{item?.actualLiveDate && fDate(item?.actualLiveDate, 'yyyy/MM/dd') || '--'}</DescriptionValue>
                                        </DescriptionItem>
                                        <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                                        <DescriptionItem>
                                            <DescriptionLabel>退租日期：</DescriptionLabel>
                                            <DescriptionValue>{item?.actualOutDate && fDate(item?.actualOutDate, 'yyyy/MM/dd') || '--'}</DescriptionValue>
                                        </DescriptionItem>
                                    </>
                                )
                            }

                            {/* 待入駐，已取消 */}
                            {
                                [
                                    TODO_STATUS_CODE.waitToOk,
                                    TODO_STATUS_CODE.waitToFail,
                                    TODO_STATUS_CODE.canceled
                                ].includes(item?.status) && (
                                    <>
                                        <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                                        <DescriptionItem>
                                            <DescriptionLabel>需求日期：</DescriptionLabel>
                                            <DescriptionValue>{item?.startDate && fDate(item?.startDate, 'yyyy/MM/dd') || '--'} - {item?.endDate && fDate(item?.endDate, 'yyyy/MM/dd') || '--'}</DescriptionValue>
                                        </DescriptionItem>
                                    </>
                                )
                            }

                            
                            {/* <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                            <DescriptionItem>
                                <DescriptionLabel>預計退租日期：</DescriptionLabel>
                                <DescriptionValue>{item?.endDate || '--'}</DescriptionValue>
                            </DescriptionItem> */}
                        </Stack>
                    </Stack>
                }
            />
            {action}
        </ListItemButton>
    )
};


const SimListButtonCell = ({ item, action }) => {
    const theme = useTheme();
    return (
        <ListItemButton
            disableGutters
            disableRipple
            sx={{ 
                // padding: theme => theme.spacing(1.5, 2),
                padding: theme => theme.spacing(1.5, 2, 1.5, 0),
                // padding: theme => theme.spacing(1.5, 2, 1.5, 5),
                '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    width: 1,
                    height: '1px',
                    backgroundColor: theme => theme.palette.divider
                }
            }}
        >
            <ListItemText
                sx={{ m: 0 }}
                primaryTypographyProps={{
                    variant: 'h6',
                    mb: 0.5,
                }}
                secondaryTypographyProps={{
                    component: 'div',
                    sx: {
                        color: 'inherit'
                    }
                }}
                primary={
                    <Stack direction={'row'} gap={2} alignItems={'center'}>
                        <Typography variant='h6'>變更申請</Typography>
                        {item?.rentType_dictText}
                        <Label color={TODO_STATUS?.[item?.status]} variant={'outlined'}>{item?.statusName}</Label>
                    </Stack>
                }
                secondary={
                    <Stack alignItems={'flex-start'} spacing={0.5}>
                        <Stack direction={'row'} alignItems={'center'} maxHeight={24}>
                            <DescriptionItem >
                                <DescriptionLabel>表單編號：</DescriptionLabel>
                                <DescriptionValue>{item?.changeNo || '--'}</DescriptionValue>
                            </DescriptionItem>
                            <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                            <DescriptionItem >
                                <DescriptionLabel>創建日期：</DescriptionLabel>
                                <DescriptionValue>{item?.createTime && fDate(item?.createTime, 'yyyy/MM/dd') }</DescriptionValue>
                            </DescriptionItem>
                            {/*   */}
                        </Stack>

                        <Stack direction={'row'} alignItems={'center'} maxHeight={24}>
                            <DescriptionItem>
                                <DescriptionLabel>廠區：</DescriptionLabel>
                                <DescriptionValue>{item?.factoryName}</DescriptionValue> 
                            </DescriptionItem>

                            <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                            <DescriptionItem>
                                <DescriptionLabel>變更類型：</DescriptionLabel>
                                <DescriptionValue>{item?.changeTypeName}</DescriptionValue>
                            </DescriptionItem>
                            <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0,  borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />

                            {
                                [ORDER_CHANGE_TYPE.Renewal].includes(item?.changeType) ? (
                                    <>
                                        <DescriptionItem>
                                            <DescriptionLabel>續租截止日期：</DescriptionLabel>
                                            <DescriptionValue>{item?.continueRentEndDate && fDate(item?.continueRentEndDate, 'yyyy/MM/dd')}</DescriptionValue>
                                        </DescriptionItem>
                                    </>
                                ) : (
                                    <DescriptionItem>
                                        <DescriptionLabel>變更開始日期：</DescriptionLabel>
                                        <DescriptionValue>{item?.changeRentStartDate && fDate(item?.changeRentStartDate, 'yyyy/MM/dd')}</DescriptionValue>
                                    </DescriptionItem>
                                )
                            }

                        </Stack>
                    </Stack>
                }
            />
            {action}
        </ListItemButton>
    )
};

// variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'quaternary', 'alcohol', 'carbon', 'download', 'notification', 'feedback']),
export default function SimNestedList({
    variant = 'apply',
    subheader,
    disableScrollTop = false,
    list = [],
    page = 1,
    defaultPage = 1,
    rowsPerPage = 10,
    total = 0,
    count = 0,
    onChangePage,
    onChangeRowsPerPage,
    onLink,
    onDelete,
    onEdit,
    onFinish,
    onCancel,
    onChange,
    loading = false,
    itemMinHeight = 72,
    hidePagination = false,
    disabledRowsEmpty = false,
    hideHeader = false,
    filterTools,
    noDataSecondary,
    noDataMinHeight = 500,
 }) {
    const listRef = useRef(null);
    const theme = useTheme();

    const [open, setOpen] = useState(-1);

    const handleClick = (index) => {
        setOpen(isOpen => isOpen === index ? -1 : index);
    };

    // 页码跳转
    const onQuickJumper = async (event) => {
        if(event?.target?.value && Number(event?.target?.value) > 0 && Number(event?.target?.value) <= Number(count)) {
            await onChangePage(event, Number(event.target.value));
        }
        if(event?.target?.value && Number(event?.target?.value) > 0 && Number(event?.target?.value) > Number(count)) {
            await onChangePage(event, Number(count));
        }
    };

    const row = (item, action) => {
        return {
            apply:      <SimListApplyBItem theme={theme} item={item} action={action} onClick={(event) => onLink(event, item)}  />,
            wait:       <SimListWaitBItem theme={theme} item={item} action={action} onClick={(event) => onLink(event, item)}  />,
            draft:      <SimListDraftBItem theme={theme} item={item} action={action} onClick={(event) => onLink(event, item)}  />,
            history:    <SimListHistoryBItem theme={theme} item={item} action={action} onClick={(event) => onLink(event, item)}  />,
        }
    };

  const emptyRows = list?.length < Number(rowsPerPage) ? Math.floor(10 - list?.length) : 0;

  return (
    <Stack spacing={3}>

        {
            (!hideHeader) && (
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <Typography variant='h6' minHeight={40} display={'flex'} alignItems={'center'}>
                    共&nbsp;
                    <Typography variant='h6' color={theme.palette.action.selected} component={'span'}>{total}</Typography> 
                    &nbsp;筆相關資料
                    </Typography>
                    {filterTools}
                </Stack>
            )
        }

        <Stack>
            <Box 
                ref={listRef} 
                position={'relative'} 
                overflow={'hidden'}
                // minHeight={emptyRows > 0 && (!disabledRowsEmpty) ? itemMinHeight * Number(rowsPerPage) : 50} 
                // minHeight={(((listRef.current?.scrollHeight) / Number(rowsPerPage)) * Number(list?.length)) > 120 ? (((listRef.current?.scrollHeight) / Number(rowsPerPage)) * Number(list?.length)) : 120}
                minHeight={(itemMinHeight * Number(list?.length)) > 120 ? (itemMinHeight * Number(list?.length)) : 120}
                sx={{
                    transition: 'all 0.26s linear',
                    transformOrigin: 'top',
                }}
            >
                <Backdrop
                    sx={{
                    position: 'absolute',
                    textAlign: 'center',
                    height: '100%',
                    zIndex: theme.zIndex.drawer + 1,
                    backgroundColor: 'transparent'
                    }}
                    open={Boolean(loading)}
                >
                    <LoadingScreen sx={{ alignItems: 'flex-start', minHeight: 'auto' }} />
                </Backdrop>
                {/* <Box position={'absolute'} top={0}>
                    {`${((listRef.current?.scrollHeight) / Number(rowsPerPage)) * Number(list?.length)}px`}
                </Box> */}
                {
                    total > 0 ? (
                        <List 
                            disablePadding
                            component={Stack}
                            spacing={2}
                            aria-labelledby="nested-list-subheader"
                        >
                            {
                                _.map(list, (item, index) => {
                                    return (
                                        <Box 
                                            key={index}
                                            bgcolor={'#FDFDFD'}
                                            borderRadius={'4px'}
                                            border={`1px solid ${theme.palette.color.backgroundBorder}`}
                                        > 

                                            
                                            {row(item, (
                                                <Stack direction={'row'} alignItems={'center'}>
                                                    {/* 我的申請{apply status} : */}
                                                    {/* 簽核中： [查看，終止] */}
                                                    {/* 退回申請人：[編輯，終止] */}
                                                    {/* 已終止：[查看，刪除] */}
                                                    {/* 待入駐：[查看，取消] */}
                                                    {/* 已入駐：[查看，變更，展開查看變更記錄] */}
                                                    {/* 已通過：[查看] */}


                                                    {/* 我的候補{wait status}: */}
                                                    {/* 簽核中： [查看，終止] */}
                                                    {/* 退回申請人：[編輯，終止] */}
                                                    {/* 已終止：[查看，刪除] */}
                                                    {/* 候補中：[查看，取消] */}


                                                    {/* 我的草稿: */}
                                                    {/* 草稿：[編輯，刪除] */} 

                                                    {/* 歷史表單: */}
                                                    {/* 已入駐：[查看，展開查看變更記錄] */}


                                                    {
                                                        [
                                                            // 申請單可查看狀態
                                                            TODO_STATUS_CODE.signing,
                                                            TODO_STATUS_CODE.terminated, 
                                                            TODO_STATUS_CODE.waitIn,
                                                            TODO_STATUS_CODE.lived,
                                                            TODO_STATUS_CODE.passed,
                                                            // 候補單可查看狀態
                                                            TODO_STATUS_CODE.waiting,
                                                            TODO_STATUS_CODE.waitToOk,
                                                            TODO_STATUS_CODE.waitToFail,
                                                            TODO_STATUS_CODE.refunded,
                                                            TODO_STATUS_CODE.canceled,
                                                         ].includes(item?.status) && (
                                                            <Typography variant={'subtitle1'} fontWeight={700} color={'primary.main'} px={1} onClick={(event) => onLink?.(event, item, variant)}>查看</Typography>
                                                        )
                                                    }


                                                    {/* 編輯 */}
                                                    {
                                                        [
                                                            TODO_STATUS_CODE.save,
                                                            TODO_STATUS_CODE.rejected,
                                                        ].includes(item?.status) && (
                                                            <>
                                                                {/* <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0, borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem /> */}

                                                                <Typography variant={'subtitle1'} fontWeight={700} color={'primary.main'} px={1} onClick={(event) => onEdit?.(event, item, variant)}>編輯</Typography>
                                                            </>
                                                        )
                                                    }

                                                    {/* 變更 */}
                                                    {
                                                        [
                                                            TODO_STATUS_CODE.lived, 
                                                        ].includes(item?.status) && (
                                                            <>
                                                                <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0, borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                                                    
                                                                <Typography variant={'subtitle1'} fontWeight={700} color={'primary.main'} px={1} onClick={(event) => onChange?.(event, item, variant)}>變更</Typography>
                                                            </>
                                                        )
                                                    }


                                                    {/* 終止 */}
                                                    {
                                                        [
                                                            TODO_STATUS_CODE.signing,
                                                            TODO_STATUS_CODE.rejected, 
                                                        ].includes(item?.status) && (
                                                            <>
                                                                <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0, borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                                                    
                                                                <WaringFormOverlay 
                                                                    primary={
                                                                        <Typography variant={'subtitle1'} fontWeight={700} color={'error.main'} px={1}>
                                                                            終止
                                                                        </Typography>
                                                                    }
                                                                    title={
                                                                        <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                                                                            <SvgWarningFill />
                                                                            確認終止簽核？
                                                                        </Stack>
                                                                    }
                                                                    description={`終止後，將停止簽核流程，不可復原。`}
                                                                    placeholder={`終止簽核需填寫簽核意見`}
                                                                    cancelText={`我再想想`}
                                                                    confirmText={`確認終止`}
                                                                    onConfirm={(value) => onFinish?.(_, item, value, variant)}
                                                                />
                                                            </>
                                                        )
                                                    }


                                                    {/* 取消 */}
                                                    {
                                                        [
                                                            TODO_STATUS_CODE.waitIn,
                                                        ].includes(item?.status) && (
                                                            <>
                                                                <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0, borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />

                                                                <WaringFormOverlay 
                                                                    primary={
                                                                        <Typography variant={'subtitle1'} fontWeight={700} color={'error.main'} px={1}>
                                                                            取消
                                                                        </Typography>
                                                                    }
                                                                    title={
                                                                        <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                                                                            <SvgWarningFill />
                                                                            確認取消入駐？
                                                                        </Stack>
                                                                    }
                                                                    description={`取消後，將放棄這筆共享辦公申請，您不可入駐使用。`}
                                                                    placeholder={`取消入駐需填寫原因`}
                                                                    cancelText={`我再想想`}
                                                                    confirmText={`確認取消`}
                                                                    onConfirm={(value) => onCancel?.(_, item, value, variant)}
                                                                />
                                                            </>
                                                        )
                                                    }
                                                    {
                                                        [
                                                            TODO_STATUS_CODE.waiting, 
                                                        ].includes(item?.status) && (
                                                            <>
                                                                <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0, borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                                                                <WaringFormOverlay 
                                                                    primary={
                                                                        <Typography variant={'subtitle1'} fontWeight={700} color={'error.main'} px={1}>
                                                                            取消
                                                                        </Typography>
                                                                    }
                                                                    title={
                                                                        <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                                                                            <SvgWarningFill />
                                                                            確認取消候補？
                                                                        </Stack>
                                                                    }
                                                                    description={`取消後，將放棄這筆共享辦公候補，您不可候補使用。`}
                                                                    placeholder={`取消候補需填寫原因`}
                                                                    cancelText={`我再想想`}
                                                                    confirmText={`確認取消`}
                                                                    onConfirm={(value) => onCancel?.(_, item, value, variant)}
                                                                />
                                                            </>
                                                        )
                                                    }

                                                    {/* 刪除 */}
                                                    {
                                                        [
                                                            TODO_STATUS_CODE.save,
                                                            TODO_STATUS_CODE.terminated, 
                                                        ].includes(item?.status) && (
                                                            <>
                                                                <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0, borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                                                
                                                                <Typography variant={'subtitle1'} fontWeight={700} color={'error.main'} px={1} onClick={(event) => onDelete?.(event, item)}>刪除</Typography>
                                                            </>
                                                        )
                                                    }

                                                    {
                                                        item?.changeList?.length > 0 && (
                                                            <>
                                                                <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0, borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />

                                                                <Tooltip
                                                                    arrow
                                                                    placement="top-end"
                                                                    title={
                                                                        <Typography component={'span'} variant='body1' noWrap>展開查看變更記錄</Typography>
                                                                    }
                                                                    PopperProps={{
                                                                        sx: {
                                                                            borderRadius: 6/6,
                                                                        },
                                                                    }}
                                                                    slotProps={{
                                                                        popper: {
                                                                            sx: {},
                                                                        },
                                                                        tooltip: {
                                                                            // sx: {
                                                                            //     borderRadius: 6/9,
                                                                            //     p: '8px 12px',
                                                                            //     boxShadow: '0px 2px 8px 0px rgba(157, 172, 208, 0.25)',
                                                                            // },
                                                                        }
                                                                    }}
                                                                >
                                                                    <Button
                                                                        variant='text'
                                                                        size='small'
                                                                        sx={{
                                                                            p: 0,
                                                                            mx: 1,
                                                                            minWidth: 24,
                                                                            borderRadius: '4px',
                                                                            transition: 'all 0.26s ease',
                                                                            transform: `rotate(${open === index ? 180 : 0}deg)`,
                                                                        }}
                                                                        onClick={() => handleClick(index)}
                                                                    >
                                                                        <SvgChevronDownFill color={theme.palette.text.primary} />
                                                                    </Button>
                                                                </Tooltip>
                                                            </>
                                                        )
                                                    }
                                                    
                                                </Stack>
                                            ))[variant]}

                                            <Collapse in={open === index} timeout="auto" unmountOnExit>
                                                {/* <Divider component="div" sx={{ my: 2 }} /> */}
                                                <List
                                                    disablePadding
                                                    component={Stack}
                                                    spacing={0}
                                                    sx={{ pl: 5 }}
                                                    aria-labelledby="nested-list-subheader"
                                                >
                                                    {
                                                        _.map(item?.changeList, (cell, cellIndex) => {
                                                            return (
                                                                <Fragment key={cellIndex}>
                                                                    {/* { (cellIndex !== 0) &&  <Divider component="div" sx={{ my: 2 }} /> } */}
                                                                    <SimListButtonCell 
                                                                        item={cell}
                                                                        action={
                                                                            <Stack direction={'row'} alignItems={'center'}>
                                                                                {/* 查看 */}
                                                                                {
                                                                                    [
                                                                                        TODO_STATUS_CODE.signing,
                                                                                        TODO_STATUS_CODE.passed,
                                                                                        TODO_STATUS_CODE.terminated,
                                                                                    ].includes(cell?.status) && (
                                                                                        <Typography variant={'subtitle1'} fontWeight={700} color={'primary.main'} onClick={(event) => onLink?.(event, { parent: item, ...cell }, ORDER_DETAILS_TYPE.Change)}>查看</Typography>
                                                                                    )
                                                                                }
                                                                                
                                                                                {/* <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0, borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem /> */}

                                                                                {/* 編輯 */}
                                                                                {
                                                                                    [
                                                                                        TODO_STATUS_CODE.rejected,
                                                                                    ].includes(cell?.status) && (
                                                                                        <Typography variant={'subtitle1'} fontWeight={700} color={'primary.main'} onClick={(event) => onEdit?.(event, { parent: item, ...cell }, ORDER_DETAILS_TYPE.Change)}>編輯</Typography>
                                                                                    )
                                                                                }
                                                                                
                                                                                {/* 終止 */}
                                                                                {
                                                                                    [
                                                                                        TODO_STATUS_CODE.signing,
                                                                                        TODO_STATUS_CODE.rejected,
                                                                                    ].includes(cell?.status) && (
                                                                                        <>
                                                                                            <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0, borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                                                                                    
                                                                                            <WaringFormOverlay 
                                                                                                primary={
                                                                                                    <Typography variant={'subtitle1'} fontWeight={700} color={'error.main'}>
                                                                                                        終止
                                                                                                    </Typography>
                                                                                                }
                                                                                                title={
                                                                                                    <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                                                                                                        <SvgWarningFill />
                                                                                                        確認終止簽核？
                                                                                                    </Stack>
                                                                                                }
                                                                                                description={`終止後，將停止簽核流程，不可復原。`}
                                                                                                placeholder={`終止簽核需填寫簽核意見`}
                                                                                                cancelText={`我再想想`}
                                                                                                confirmText={`確認終止`}
                                                                                                onConfirm={(value) => onFinish?.(_, cell, value, ORDER_DETAILS_TYPE.Change)}
                                                                                            />
                                                                                        </>
                                                                                    )
                                                                                }
                                                                                
                                                                                {/* 取消 */}
                                                                                {/* {
                                                                                    [
                                                                                        TODO_STATUS_CODE.signing,
                                                                                        TODO_STATUS_CODE.rejected,
                                                                                    ].includes(cell?.status) && (
                                                                                        <>
                                                                                            <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0, borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                                                                                
                                                                                            <Typography variant={'subtitle1'} fontWeight={700} color={'error.main'}>取消</Typography>
                                                                                        </>
                                                                                    )
                                                                                } */}
                                                                                
                                                                                
                                                                                {
                                                                                    [
                                                                                        TODO_STATUS_CODE.terminated
                                                                                    ].includes(cell?.status) && (
                                                                                        <>
                                                                                            <Divider sx={{ width: '1px', height: 16, alignSelf: 'center', mx: 1, my: 0, borderColor: '#D8D8D8' }} color={'#D8D8D8'} orientation="vertical" variant="middle" flexItem />
                                                                                
                                                                                            <Typography variant={'subtitle1'} fontWeight={700} color={'error.main'} onClick={(event) => onDelete?.(event, cell, ORDER_DETAILS_TYPE.Change)}>刪除</Typography>
                                                                                        </>
                                                                                    )
                                                                                }
                                                                                
                                                                            </Stack>
                                                                        }
                                                                    />
                                                                </Fragment>
                                                            )
                                                        })
                                                    }
                                                </List>
                                            </Collapse>
                                        </Box>
                                    )
                                })
                            }
                            {emptyRows > 0 && (!disabledRowsEmpty) && (
                                <Box sx={{ height: ((listRef.current?.scrollHeight) / Number(rowsPerPage)) * emptyRows }} />
                            )}
                        </List>
                    ) : (
                        loading ? (
                            _.map(Array.from({ length: Number(rowsPerPage) }), (item, index) => {
                            return (
                                <Box key={index} sx={{ mb: 0.5 }}>
                                    {/* <Skeleton variant="rectangular" width={`100%`} height={itemMinHeight} /> */}
                                </Box>)
                            })
                        ) : (
                            <NoData secondary={noDataSecondary} minHeight={noDataMinHeight} />
                        )
                    )
                }
            </Box>

            {
                (!hidePagination) && (
                    <SimPagination
                        disableScrollTop={disableScrollTop}
                        total={Number(total)}
                        page={Number(page)}
                        defaultPage={Number(defaultPage)}
                        rowsPerPage={Number(rowsPerPage)}
                        count={Number(count)}
                        onChangePage={onChangePage}
                        onChangeRowsPerPage={onChangeRowsPerPage}
                        onQuickJumper={onQuickJumper}
                    />
                )
            }
            
        </Stack>
    </Stack>
  );
}
